/**
 * Форматирование чисел для интерфейса: деньги, годы, порядок среди детей.
 *
 * Деньги в движке — доллары ППС. Рядом показывается «ощущаемая сумма»
 * в рублях по ППС России (`RUB_PPP`, моя оценка 2024 года), округлённая
 * до двух значащих цифр — это ориентир, а не курс. Разряды разделяются
 * неразрывным пробелом, чтобы сумма не рвалась на границе строки.
 */
import { t, yearsWord } from '@life/i18n';
import type { Sex } from '@life/engine';

/** Рублей за доллар по паритету покупательной способности. */
export const RUB_PPP = 30.7;
const NBSP = ' ';

/** Целое с разрядами через неразрывный пробел: 1 240 000. */
export function groupThousands(n: number): string {
  const abs = Math.round(Math.abs(n));
  const digits = String(abs);
  const groups: string[] = [];
  for (let end = digits.length; end > 0; end -= 3) groups.unshift(digits.slice(Math.max(0, end - 3), end));
  return (n < 0 ? '−' : '') + groups.join(NBSP);
}

/** Округление до `sig` значащих цифр. */
function roundSig(x: number, sig: number): number {
  if (x === 0 || !Number.isFinite(x)) return 0;
  const p = 10 ** (sig - 1 - Math.floor(Math.log10(Math.abs(x))));
  return Math.round(x * p) / p;
}

/** «1 240 $ · ≈ 38 000 ₽». */
export function money(ppp: number): string {
  const usd = groupThousands(ppp);
  const rub = groupThousands(roundSig(ppp * RUB_PPP, 2));
  return `${usd}${NBSP}$ · ≈${NBSP}${rub}${NBSP}₽`;
}

/** «15 лет», «3 года», «1 год». */
export function years(n: number): string {
  return `${n} ${yearsWord(n)}`;
}

/** «ребёнок / ребёнка / детей» после числа. */
export function childrenWord(n: number): string {
  const abs = Math.abs(Math.trunc(n));
  const tens = abs % 100;
  const ones = abs % 10;
  if (tens >= 11 && tens <= 19) return t('ui.word.child.many');
  if (ones === 1) return t('ui.word.child.one');
  if (ones >= 2 && ones <= 4) return t('ui.word.child.few');
  return t('ui.word.child.many');
}

function ordinal(sex: Sex, n: number): string {
  return n >= 1 && n <= 10 ? t(`ui.ordinal.${sex}.${n}`) : t(`ui.ordinal.${sex}.n`, { n });
}

/** «четвёртая из шести детей» или «единственный ребёнок в семье». */
export function ordinalChild(n: number, total: number, sex: Sex): string {
  if (total <= 1) return t('ui.birth.children.only');
  return t('ui.birth.children', { order: ordinal(sex, n), total });
}
